import {Component, Input, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {FormArray, FormControl, FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {Goal} from '../../../../../models/goal';
import {GoalType} from '../../../../../models/goal-type';
import {Offer} from '../../../../../models/offer';
import {OfferGoal} from '../../../../../models/offer-goal';
import {OfferGoalType} from '../../../../../models/offer-goal-type';
import {GoalService} from '../../../../../services/goal/goal.service';
import {OfferService} from '../../../../../services/offer/offer.service';
import {LoaderService} from '../../../../../services/loader/loader.service';
import {SnackbarService} from '../../../../../services/snackbar/snackbar.service';
import {InputSwitchComponent} from '../../../../layout/input-switch/input-switch.component';
import {DropdownSelectComponent} from '../../../../layout/dropdown-select/dropdown-select.component';
import {
  DropdownSelectOptionComponent
} from '../../../../layout/dropdown-select-option/dropdown-select-option.component';

export type OfferGoalTypeSetupForm = FormGroup<{
  goalTypeId: FormControl<number | null>;
  name: FormControl<string | null>;
  minAge: FormControl<number | null>;
  payoutAmount: FormControl<number | null>;
  payoutPercent: FormControl<number | null>;
}>;

export type OfferGoalSetupForm = FormGroup<{
  goalId: FormControl<number | null>;
  private: FormControl<boolean | null>;
  multiple: FormControl<boolean | null>;
  minAge: FormControl<number | null>;
  payoutAmount: FormControl<number | null>;
  payoutPercent: FormControl<number | null>;
  types: FormArray<OfferGoalTypeSetupForm>;
}>;

@Component({
  selector: 'app-offer-goal-setup',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    InputSwitchComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent
  ],
  templateUrl: './offer-goal-setup.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './offer-goal-setup.component.scss'
})
export class OfferGoalSetupComponent implements OnInit {
  @Input({required: true}) goals!: FormArray<OfferGoalSetupForm>;
  @Input() required = true;
  @Input() submitted = false;

  loaded = false;
  availableGoals: Goal[] = [];
  sourceOffers: Offer[] = [];
  sourceOfferId: number | null = null;
  selectedGoalTypes: Record<number, GoalType | null> = {};

  constructor(
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private goalService: GoalService,
    private offerService: OfferService,
  ) {
  }

  ngOnInit(): void {
    forkJoin([
      this.goalService.findAll({groups: 'types', sort: 'name'}),
      this.offerService.findAll({targetAccountType: 'affiliate', sort: 'name'}),
    ]).subscribe(([goals, offers]) => {
      this.availableGoals = goals;
      this.sourceOffers = offers;
      this.loaded = true;
    });
  }

  findGoalForm(goal: Goal): OfferGoalSetupForm | null {
    return this.goals.controls.find(control => control.value.goalId === goal.id) ?? null;
  }

  toggleGoal(goal: Goal, e: any): void {
    if (e.target.checked) {
      this.addGoal(goal);
    } else {
      const i = this.goals.controls.findIndex(control => control.value.goalId === goal.id);
      if (i >= 0) {
        this.goals.removeAt(i);
      }
    }
  }

  addGoal(goal: Goal, offerGoal: OfferGoal | null = null): OfferGoalSetupForm {
    const form: OfferGoalSetupForm = new FormGroup({
      goalId: new FormControl<number | null>(goal.id),
      private: new FormControl<boolean | null>(offerGoal?.private ?? false),
      multiple: new FormControl<boolean | null>(offerGoal?.multiple ?? false),
      minAge: new FormControl<number | null>(this.positiveOrNull(offerGoal?.minAge)),
      payoutAmount: new FormControl<number | null>(this.positiveOrNull(offerGoal?.payoutAmount)),
      payoutPercent: new FormControl<number | null>(offerGoal?.payoutPercent ? offerGoal.payoutPercent * 100 : null),
      types: new FormArray<OfferGoalTypeSetupForm>([]),
    });

    offerGoal?.types.forEach((offerGoalType: OfferGoalType) => {
      if (offerGoalType.goalType) {
        this.pushGoalType(form, offerGoalType.goalType, offerGoalType);
      }
    });

    this.goals.push(form);

    return form;
  }

  getAvailableGoalTypes(goal: Goal): GoalType[] {
    const form = this.findGoalForm(goal);
    const usedIds = form?.controls.types.controls.map(control => control.value.goalTypeId) ?? [];

    return goal.types.filter((goalType: GoalType) => !usedIds.includes(goalType.id));
  }

  addGoalType(goal: Goal): void {
    const form = this.findGoalForm(goal);
    const goalType = this.selectedGoalTypes[goal.id!];
    if (form && goalType) {
      this.pushGoalType(form, goalType);
      this.selectedGoalTypes[goal.id!] = null;
    }
  }

  removeGoalType(goalForm: OfferGoalSetupForm, i: number): void {
    goalForm.controls.types.removeAt(i);
  }

  copyFromOffer(offerId: number | null): void {
    if (!offerId) {
      return;
    }

    this.loader.startLoading();
    this.offerService.findOneById(offerId, {groups: 'goals,goal,goalTypes,goalType'}).subscribe((offer: Offer) => {
      this.loader.stopLoading();

      if (!offer.offerGoals.length) {
        this.snackbar.error('This offer has no goal to copy.');
        return;
      }

      this.goals.clear();
      offer.offerGoals.forEach((offerGoal: OfferGoal) => {
        const goal = this.availableGoals.find(g => g.id === offerGoal.goal?.id);
        if (goal) {
          this.addGoal(goal, offerGoal);
        }
      });
      this.snackbar.success('Goals copied from ' + offer.name + '.');
    });
  }

  isMissing(): boolean {
    return this.required && this.goals.length === 0;
  }

  private pushGoalType(form: OfferGoalSetupForm, goalType: GoalType, offerGoalType: OfferGoalType | null = null): void {
    form.controls.types.push(new FormGroup({
      goalTypeId: new FormControl<number | null>(goalType.id),
      name: new FormControl<string | null>(goalType.name),
      minAge: new FormControl<number | null>(this.positiveOrNull(offerGoalType?.minAge)),
      payoutAmount: new FormControl<number | null>(this.positiveOrNull(offerGoalType?.payoutAmount)),
      payoutPercent: new FormControl<number | null>(offerGoalType?.payoutPercent ? offerGoalType.payoutPercent * 100 : null),
    }));
  }

  private positiveOrNull(value: number | null | undefined): number | null {
    return value && value > 0 ? value : null;
  }
}

export function toOfferGoalsPayload(goals: FormArray<OfferGoalSetupForm>): any[] {
  const intOrNull = (value: any): number | null => typeof value === 'number' && value >= 1 ? Math.round(value) : null;
  const amountOrNull = (value: any): number | null => typeof value === 'number' && value >= 0.01 ? value : null;
  const percentOrNull = (value: any): number | null => typeof value === 'number' ? value / 100.0 : null;

  return goals.getRawValue().map(goal => ({
    goalId: goal.goalId,
    private: !!goal.private,
    multiple: !!goal.multiple,
    minAge: intOrNull(goal.minAge),
    payoutAmount: amountOrNull(goal.payoutAmount),
    payoutPercent: percentOrNull(goal.payoutPercent),
    types: goal.types.map(type => ({
      goalTypeId: type.goalTypeId,
      minAge: intOrNull(type.minAge),
      payoutAmount: amountOrNull(type.payoutAmount),
      payoutPercent: percentOrNull(type.payoutPercent),
    })),
  }));
}
